import React, { lazy, Suspense, useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import PerformanceMonitor from './components/common/PerformanceMonitor';
import SplashScreen from './components/common/SplashScreen';
import GradeCoursesPage from './pages/GradeCoursesPage';
import BookGradePage from './pages/BookGradePage';
import Preparatory3CoursesPage from './pages/Preparatory3CoursesPage';


// Pages
import Home from './pages/Home';
import About from './pages/About';
import ForgotPassword from './pages/ForgotPassword';

// Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import Profile from './components/user/Profile';
import EnrolledCourses from './components/user/EnrolledCourses';
import CourseDetail from './components/course/CourseDetail';
import CourseViewer from './components/course/CourseViewer';
import RefreshTokenTest from './components/test/RefreshTokenTest';
import MyLectures from './pages/MyLectures';
import Statistics from './pages/Statistics';
import ParentMonitoring from './pages/ParentMonitoring';
import CourseCurriculum from './pages/CourseCurriculum';
import LessonVideoPlayer from './pages/LessonVideoPlayer';
import LessonQuizPlayer from './pages/LessonQuizPlayer';
import CourseViewerRedirect from './components/course/CourseViewerRedirect';
import UnitViewer from './components/unit/UnitViewer';

function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    AOS.init({
      duration: 2700,
      easing: 'ease-out-quad',
      once: true,
      offset: 80,
      mirror: false,
      anchorPlacement: 'top-bottom',
      disable: prefersReducedMotion,
      debounceDelay: 50,
      throttleDelay: 99,
    });
    setTimeout(() => AOS.refresh(), 0);
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} duration={3000} />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <CourseProvider>
              <div className="min-h-screen bg-slate-50">
                <Routes>
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/parent-monitoring" element={<ParentMonitoring />} />
            {/* Grade Courses Routes */}
            <Route path="/courses/grade/:grade" element={<GradeCoursesPage />} />
            
            {/* Preparatory 3 Courses Route */}
            <Route path="/courses/grade/preparatory3" element={<Preparatory3CoursesPage />} />
            
            {/* Grade Books Routes */}
            <Route path="/books/grade/:grade" element={<BookGradePage />} />

                    
                    {/* Protected Routes */}
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />
                    <Route path="/my-lectures" element={
                      <ProtectedRoute>
                        <MyLectures />
                      </ProtectedRoute>
                    } />
                    <Route path="/enrolled-courses" element={
                      <ProtectedRoute>
                        <EnrolledCourses />
                      </ProtectedRoute>
                    } />
                    <Route path="/statistics" element={
                      <ProtectedRoute>
                        <Statistics />
                      </ProtectedRoute>
                    } />
                    <Route path="/courses/grade/:grade" element={<GradeCoursesPage />} />
                    <Route path="/courses/:id" element={<CourseDetail />} />
                    {/* Test Route */}
                    <Route path="/test-refresh-token" element={
                      <ProtectedRoute>
                        <RefreshTokenTest />
                      </ProtectedRoute>
                    } />
                  </Route>
                  {/* New Course Structure - Curriculum and Player Pages */}
                  <Route path="/course/:courseId/curriculum" element={
                    <ProtectedRoute>
                      <CourseCurriculum />
                    </ProtectedRoute>
                  } />
                  <Route path="/course/:courseId/lesson/:lessonId/video" element={
                    <ProtectedRoute>
                      <LessonVideoPlayer />
                    </ProtectedRoute>
                  } />
                  <Route path="/course/:courseId/lesson/:lessonId/quiz" element={
                    <ProtectedRoute>
                      <LessonQuizPlayer />
                    </ProtectedRoute>
                  } />
                  
                  {/* Legacy CourseViewer - Redirect to new structure */}
                  <Route path="/course-player/:courseId" element={
                    <ProtectedRoute>
                      <CourseViewerRedirect />
                    </ProtectedRoute>
                  } />
                  {/* UnitViewer without MainLayout (no Navbar/Footer) */}
                  <Route path="/unit-viewer/:unitId" element={
                    <ProtectedRoute>
                      <UnitViewer />
                    </ProtectedRoute>
                  } />
                </Routes>
                
                {/* Performance Monitor - Only in Development */}
                <PerformanceMonitor />
              </div>
            </CourseProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
